import { useMemo } from 'react'
import {
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { NumberFormatter } from '@mantine/core'
import {
  DEFAULT_PAGINATION_SIZE,
  tableDefaultAlertBannerProps,
  tableDefaultProps,
} from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { ListBaleDetailsOrderByOptions, ListBaleDetailsResponse } from '@shared/types'

interface BaleDetailsTableProps {
  data?: ListBaleDetailsResponse | null
  renderKey?: string
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  sorting?: SortingState
  setSorting?: OnChangeFn<SortingState>
  pagination?: PaginationState
  setPagination?: OnChangeFn<PaginationState>
  columnFilters?: ColumnFiltersState
  setColumnFilters?: OnChangeFn<ColumnFiltersState>
}

export const BaleDetailsTable = ({
  data = null,
  renderKey = '',
  noDataMessage = 'No bale details available for this station',
  isLoading,
  isError,
  sorting = [],
  setSorting,
  pagination = { pageIndex: 0, pageSize: DEFAULT_PAGINATION_SIZE },
  setPagination,
  columnFilters,
  setColumnFilters,
}: BaleDetailsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: ListBaleDetailsOrderByOptions.BaleNumber,
        accessorKey: 'baleNumber',
        header: 'Bale No',
        enableSorting: true,
        sortDescFirst: false,
        Footer: () => <>Totals</>,
        filterVariant: 'text',
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: ListBaleDetailsOrderByOptions.LineNumber,
        accessorKey: 'lineNumber',
        header: 'Line No',
        enableSorting: true,
        sortDescFirst: false,
        enableColumnFilter: false,
      },
      {
        id: 'netKGs',
        accessorKey: 'netKGs',
        header: 'Net KGs',
        Cell: ({ cell }) => (
          <NumberFormatter value={cell.getValue<number>()} thousandSeparator decimalScale={1} />
        ),
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
        enableColumnFilter: false,
        enableSorting: false,
        Footer: () => (
          <NumberFormatter value={data?.totalNetKGs} thousandSeparator decimalScale={1} />
        ),
      },
      {
        id: 'referenceNumber',
        accessorKey: 'referenceNumber',
        header: 'Reference No',
        enableSorting: false,
        filterVariant: 'text',
      },
      {
        id: 'sale',
        accessorKey: 'sale',
        header: 'Sale No',
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'lotNumber',
        accessorKey: 'lotNumber',
        header: 'Lot No',
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'saleOutcome',
        accessorKey: 'saleOutcome',
        header: 'Sale Outcome',
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [data?.totalNetKGs]
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data: data?.results ?? [],
    state: {
      isLoading,
      sorting,
      columnFilters,
      pagination,
      showAlertBanner: isError,
    },
    initialState: {
      ...tableDefaultProps.initialState,
      showColumnFilters: true,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Bale Details',
    },
    // Sorting
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
    enableMultiSort: false,
    // Pagination
    manualPagination: true,
    rowCount: data?.totalResultCount,
    pageCount: data?.totalPageCount,
    onPaginationChange: setPagination,
    // Column filters
    enableFilters: true,
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,
    enableFilterMatchHighlighting: false,
  })

  return <MantineReactTable table={table} key={renderKey} />
}
